// _redux/actions/ActionCallCenter.js
import { callCenterApi } from '@/_services/api/callCenter';
import { toast } from 'react-toastify';

export const CALL_CENTER_ACTION_TYPES = {
    // UI State
    SET_LOADING: 'CALL_CENTER_SET_LOADING',
    SET_ERROR: 'CALL_CENTER_SET_ERROR',
    CLEAR_ERROR: 'CALL_CENTER_CLEAR_ERROR',

    // Contacts
    SET_CONTACTS: 'CALL_CENTER_SET_CONTACTS',
    ADD_CONTACT: 'CALL_CENTER_ADD_CONTACT',
    UPDATE_CONTACT: 'CALL_CENTER_UPDATE_CONTACT',
    DELETE_CONTACT: 'CALL_CENTER_DELETE_CONTACT',

    // Presets
    SET_PRESETS: 'CALL_CENTER_SET_PRESETS',
    ADD_PRESET: 'CALL_CENTER_ADD_PRESET',
    UPDATE_PRESET: 'CALL_CENTER_UPDATE_PRESET',
    DELETE_PRESET: 'CALL_CENTER_DELETE_PRESET',

    // Labels
    SET_LABELS: 'CALL_CENTER_SET_LABELS',
    ADD_LABEL: 'CALL_CENTER_ADD_LABEL',
    UPDATE_LABEL: 'CALL_CENTER_UPDATE_LABEL',
    DELETE_LABEL: 'CALL_CENTER_DELETE_LABEL',

    // Calls
    SET_CALLS: 'CALL_CENTER_SET_CALLS',
    SET_TOTAL_CALLS: 'CALL_CENTER_SET_TOTAL_CALLS',
    ADD_CALL: 'CALL_CENTER_ADD_CALL',
    UPDATE_CALL: 'CALL_CENTER_UPDATE_CALL',
    SET_ACTIVE_CALL: 'CALL_CENTER_SET_ACTIVE_CALL',
    CLEAR_ACTIVE_CALL: 'CALL_CENTER_CLEAR_ACTIVE_CALL',

    // Real-time Updates
    CALL_STATUS_UPDATE: 'CALL_CENTER_CALL_STATUS_UPDATE',
    CALL_EVENT_UPDATE: 'CALL_CENTER_CALL_EVENT_UPDATE',
    AUDIO_DATA_UPDATE: 'CALL_CENTER_AUDIO_DATA_UPDATE',

    // Socket Connection
    SET_CALL_CONNECTION: 'CALL_CENTER_SET_CALL_CONNECTION',
    CLEAR_CALL_CONNECTION: 'CALL_CENTER_CLEAR_CALL_CONNECTION',
};

// UI Actions
export const setCallCenterLoading = (loading) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_LOADING,
    payload: loading,
});

export const setCallCenterError = (error) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_ERROR,
    payload: error,
});

export const clearCallCenterError = () => ({
    type: CALL_CENTER_ACTION_TYPES.CLEAR_ERROR,
});

// Contact Actions
export const setContacts = (contacts) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_CONTACTS,
    payload: contacts,
});

export const addContact = (contact) => ({
    type: CALL_CENTER_ACTION_TYPES.ADD_CONTACT,
    payload: contact,
});

export const updateContact = (contactId, updates) => ({
    type: CALL_CENTER_ACTION_TYPES.UPDATE_CONTACT,
    payload: { contactId, updates },
});

export const deleteContact = (contactId) => ({
    type: CALL_CENTER_ACTION_TYPES.DELETE_CONTACT,
    payload: contactId,
});

// Preset Actions
export const setPresets = (presets) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_PRESETS,
    payload: presets,
});

export const addPreset = (preset) => ({
    type: CALL_CENTER_ACTION_TYPES.ADD_PRESET,
    payload: preset,
});

export const updatePreset = (presetId, updates) => ({
    type: CALL_CENTER_ACTION_TYPES.UPDATE_PRESET,
    payload: { presetId, updates },
});

export const deletePreset = (presetId) => ({
    type: CALL_CENTER_ACTION_TYPES.DELETE_PRESET,
    payload: presetId,
});

// Label Actions
export const setLabels = (labels) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_LABELS,
    payload: labels,
});

export const addLabel = (label) => ({
    type: CALL_CENTER_ACTION_TYPES.ADD_LABEL,
    payload: label,
});

export const updateLabel = (labelId, updates) => ({
    type: CALL_CENTER_ACTION_TYPES.UPDATE_LABEL,
    payload: { labelId, updates },
});

export const deleteLabel = (labelId) => ({
    type: CALL_CENTER_ACTION_TYPES.DELETE_LABEL,
    payload: labelId,
});

// Call Actions
export const setCalls = (calls) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_CALLS,
    payload: calls,
});

export const setTotalCalls = (totalCalls) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_TOTAL_CALLS,
    payload: totalCalls,
});

export const addCall = (call) => ({
    type: CALL_CENTER_ACTION_TYPES.ADD_CALL,
    payload: call,
});

export const updateCall = (callId, updates) => ({
    type: CALL_CENTER_ACTION_TYPES.UPDATE_CALL,
    payload: { callId, updates },
});

export const setActiveCall = (call) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_ACTIVE_CALL,
    payload: call,
});

export const clearActiveCall = () => ({
    type: CALL_CENTER_ACTION_TYPES.CLEAR_ACTIVE_CALL,
});

// Real-time Update Actions
export const callStatusUpdate = (data) => ({
    type: CALL_CENTER_ACTION_TYPES.CALL_STATUS_UPDATE,
    payload: data,
});

export const callEventUpdate = (callId, event) => ({
    type: CALL_CENTER_ACTION_TYPES.CALL_EVENT_UPDATE,
    payload: { callId, event },
});

export const audioDataUpdate = (data) => ({
    type: CALL_CENTER_ACTION_TYPES.AUDIO_DATA_UPDATE,
    payload: data,
});

// Connection Actions
export const setCallConnection = (connection) => ({
    type: CALL_CENTER_ACTION_TYPES.SET_CALL_CONNECTION,
    payload: connection,
});

export const clearCallConnection = () => ({
    type: CALL_CENTER_ACTION_TYPES.CLEAR_CALL_CONNECTION,
});

// Thunk Actions

// Contacts
export const fetchContactsAction = (teamId) => async (dispatch) => {
    try {
        dispatch(setCallCenterLoading(true));
        const response = await callCenterApi.getContacts({ team: teamId });
        dispatch(setContacts(response.results || []));
        return response;
    } catch (error) {
        if (error?.name === 'AbortError') return;
        dispatch(setCallCenterError(error.message));
        toast.error('Failed to fetch contacts');
        throw error;
    } finally {
        dispatch(setCallCenterLoading(false));
    }
};

export const createContactAction = (contactData) => async (dispatch) => {
    try {
        const response = await callCenterApi.createContact(contactData);
        dispatch(addContact(response));
        toast.success('Contact created successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to create contact');
        throw error;
    }
};

export const bulkCreateContactsAction = (contacts, teamId) => async (dispatch) => {
    try {
        const response = await callCenterApi.bulkCreateContacts({ contacts, teamId });
        // Refresh contacts after bulk create
        dispatch(fetchContactsAction(teamId));
        toast.success(`Successfully created ${response.count} contacts`);
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to bulk create contacts');
        throw error;
    }
};

// Presets
export const fetchPresetsAction = (teamId) => async (dispatch) => {
    try {
        dispatch(setCallCenterLoading(true));
        const response = await callCenterApi.getPresets({ team: teamId });
        dispatch(setPresets(response.results || []));
        return response;
    } catch (error) {
        if (error?.name === 'AbortError') return;
        dispatch(setCallCenterError(error.message));
        toast.error('Failed to fetch presets');
        throw error;
    } finally {
        dispatch(setCallCenterLoading(false));
    }
};

export const createPresetAction = (presetData) => async (dispatch) => {
    try {
        const response = await callCenterApi.createPreset(presetData);
        dispatch(addPreset(response));
        toast.success('Preset created successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to create preset');
        throw error;
    }
};

export const updatePresetAction = (presetId, updates) => async (dispatch) => {
    try {
        const response = await callCenterApi.updatePreset(presetId, updates);
        dispatch(updatePreset(presetId, response));
        toast.success('Preset updated successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to update preset');
        throw error;
    }
};

export const deletePresetAction = (presetId) => async (dispatch) => {
    try {
        await callCenterApi.deletePreset(presetId);
        dispatch(deletePreset(presetId));
        toast.success('Preset deleted successfully');
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to delete preset');
        throw error;
    }
};

// Labels
export const fetchLabelsAction = (teamId) => async (dispatch) => {
    try {
        dispatch(setCallCenterLoading(true));
        const response = await callCenterApi.getLabels({ team: teamId });
        dispatch(setLabels(response.results || []));
        return response;
    } catch (error) {
        if (error?.name === 'AbortError') return;
        dispatch(setCallCenterError(error.message));
        toast.error('Failed to fetch Labels');
        throw error;
    } finally {
        dispatch(setCallCenterLoading(false));
    }
};

export const createLabelAction = (LabelData) => async (dispatch) => {
    try {
        const response = await callCenterApi.createLabel(LabelData);
        dispatch(addLabel(response));
        toast.success('Label created successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to create Label');
        throw error;
    }
};

export const updateLabelAction = (LabelId, updates) => async (dispatch) => {
    try {
        const response = await callCenterApi.updateLabel(LabelId, updates);
        dispatch(updateLabel(LabelId, response));
        toast.success('Label updated successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to update Label');
        throw error;
    }
};

export const deleteLabelAction = (LabelId) => async (dispatch) => {
    try {
        await callCenterApi.deleteLabel(LabelId);
        dispatch(deleteLabel(LabelId));
        toast.success('Label deleted successfully');
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to delete Label');
        throw error;
    }
};

// Calls
export const fetchCallsAction =
    (teamId, { page = 1, limit = 10, status, label, contact, preset } = {}) =>
    async (dispatch) => {
        try {
            dispatch(setCallCenterLoading(true));
            const response = await callCenterApi.getCalls({
                team: teamId,
                page,
                limit,
                sortBy: 'createdAt:desc',
                status,
                label,
                contact,
                preset,
            });
            dispatch(setCalls(response.results || []));
            dispatch(setTotalCalls(response.totalResults));
            return response;
        } catch (error) {
            if (error?.name === 'AbortError') return;
            dispatch(setCallCenterError(error.message));
            toast.error('Failed to fetch calls');
            throw error;
        } finally {
            dispatch(setCallCenterLoading(false));
        }
    };

export const createCallAction = (callData) => async (dispatch) => {
    try {
        const response = await callCenterApi.createCall(callData);
        dispatch(addCall(response));
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to create call');
        throw error;
    }
};

export const initiateCallAction = (callId) => async (dispatch) => {
    try {
        const response = await callCenterApi.initiateCall(callId);
        dispatch(updateCall(callId, response));
        dispatch(setActiveCall(response));
        toast.success('Call initiated successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to initiate call');
        throw error;
    }
};

export const endCallAction = (callId) => async (dispatch) => {
    try {
        const response = await callCenterApi.endCall(callId);
        dispatch(updateCall(callId, { callStatus: 'completed', endedAt: new Date().toISOString() }));
        dispatch(clearActiveCall());
        toast.success('Call ended successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to end call');
        throw error;
    }
};

export const updateCallDataAction = (callId, data) => async (dispatch) => {
    try {
        const response = await callCenterApi.updateCall(callId, data);
        dispatch(updateCall(callId, response));
        toast.success('Updated call deails successfully');
        return response;
    } catch (error) {
        toast.error(error.message || 'Failed to update call details');
        throw error;
    }
};

export const bargeIntoCallAction = (callId) => async (dispatch) => {
    try {
        const response = await callCenterApi.bargeIntoCall(callId);
        dispatch(setCallConnection(response));
        toast.success('Barged into call successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to barge into call');
        throw error;
    }
};

export const listenToCallAction = (callId) => async (dispatch) => {
    try {
        const response = await callCenterApi.listenToCall(callId);
        dispatch(setCallConnection(response));
        toast.success('Listening to call successfully');
        return response;
    } catch (error) {
        dispatch(setCallCenterError(error.message));
        toast.error(error.message || 'Failed to listen to call');
        throw error;
    }
};
